import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { useTheme } from './ThemeContext';
import App from './App';
import LoginPage from './components/pages/LoginPage';
import SubscriptionForm from './components/pages/pay';
import ProfilePage from './components/pages/ProfilePage';
import WaitingListPage from './components/pages/WaitingListPage';
import YesterdaySales from './components/pages/YesterdaySales';
import VideosPage from './components/pages/VideosPage';
import ReferralLinkPage from './components/pages/ReferralLinkPage';
import ChatPage from './components/pages/ChatPage';
import MarketplacePage from './components/pages/MarketplacePage';
import MyMarketItemsPage from './components/pages/MyMarketItemsPage';
import LoadingIndicator from './components/ui/LoadingIndicator';
import GlassToastViewport, { showGlassToast } from './components/ui/GlassToast';
import { Modal, Button } from 'react-bootstrap';
import { getMessaging, onMessage, isSupported as isMessagingSupported } from "firebase/messaging";
import { FilterProvider } from './components/FilterContext';
import { WebSocketProvider } from './components/WebSocketContext';
import PublicLanding from './components/pages/PublicLanding';
import { getFirebaseApp, isPushRuntimeEligible, isSecureRuntimeForServiceWorker } from './firebaseClient';
import { fetchLatestChangelogEntry, getLatestChangelogEntry } from './changelog';

const GLASS_MODE_STORAGE_KEY = 'salew:glass-disabled';


const ProtectedRoute = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) return <LoadingIndicator />;
  if (!isAuthenticated) return <PublicLanding />;
  return children;
};

const isStandaloneMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const navStandalone = window.navigator?.standalone === true;
  const displayModeStandalone =
    typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches;
  return navStandalone || displayModeStandalone;
};

const getBrowserInfo = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isStandalone = isStandaloneMode();
  const isInAppBrowser = /telegram|instagram|fbav|fban|vkclient|vkontakte|line\/|micromessenger|whatsapp|okhttp|wv\)/i.test(ua)
    || (isAndroid && /\bwv\b/i.test(ua));
  const safariByUa = /safari/i.test(ua)
    && !/chrome|crios|android|firefox|fxios|yabrowser|yasearch|opera|opr|wv\)/i.test(ua);

  return {
    isMobile: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua),
    isIOS,
    isAndroid,
    isChrome: /chrome/i.test(ua)
      && !/edg|edge|yabrowser|yasearch|opera|opr|samsungbrowser|firefox|fxios|ucbrowser|wv\)/i.test(ua),
    isSafari: safariByUa || (isIOS && isStandalone),
    isYandex: /yabrowser|yasearch/i.test(ua),
    isDesktop: !/(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i.test(ua),
    isInAppBrowser,
    isStandalone,
  };
};

const isPwaSupported = () => {
  const { isMobile, isIOS, isAndroid, isChrome, isSafari, isDesktop, isYandex, isInAppBrowser } = getBrowserInfo();

  if (isDesktop) return true;

  if (isMobile) {

    if (isInAppBrowser) return false;

    if (isYandex) return false;

    if (isIOS) return isSafari || isStandalone;

    if (isAndroid) return isChrome;
  }

  return false;
};


const Root = () => {
  const { theme } = useTheme();
  const themeClass = theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark';
  document.body.className = `App ${themeClass}`;

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [unsupportedBrowser, setUnsupportedBrowser] = useState(false);
  const [recommendedBrowser, setRecommendedBrowser] = useState(null);
  const [browserInfo, setBrowserInfo] = useState({});
  const [isStandalone, setIsStandalone] = useState(false);
  const [latestChangelog, setLatestChangelog] = useState(() => getLatestChangelogEntry());
  const [isGlassDisabled, setIsGlassDisabled] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.localStorage.getItem(GLASS_MODE_STORAGE_KEY) === 'true';
  });



  // Service Worker + foreground push listener
  useEffect(() => {
    let unsubscribeOnMessage = null;
    let isDisposed = false;
    let foregroundSetupStarted = false;
    let changelogRequestId = 0;

    const loadLatestChangelog = async () => {
      const requestId = changelogRequestId + 1;
      changelogRequestId = requestId;
      try {
        const entry = await fetchLatestChangelogEntry();
        if (isDisposed || changelogRequestId !== requestId || !entry) {
          return null;
        }
        setLatestChangelog(entry);
        return entry;
      } catch (error) {
        if (!isDisposed) {
          console.warn('Не удалось загрузить changelog.json:', error);
        }
        return null;
      }
    };

    const openUpdateModal = async () => {
      await loadLatestChangelog();
      if (!isDisposed) {
        setShowUpdateModal(true);
      }
    };

    const handleControllerChange = () => {
      console.log('Service Worker обновлён, перезагрузка страницы...');
      window.location.reload();
    };

    const setupServiceWorker = async () => {
      if (
        typeof navigator === 'undefined'
        || !('serviceWorker' in navigator)
        || !isSecureRuntimeForServiceWorker()
      ) {
        return;
      }

      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          updateViaCache: 'none',
        });
        registration.update().catch(() => { });

        if (registration.waiting) {
          void openUpdateModal();
        }

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;

          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed' && navigator.serviceWorker?.controller) {
              void openUpdateModal();
            }
          };
        };
      } catch (error) {
        console.error('Ошибка при регистрации Service Worker:', error);
      }

      if (navigator.serviceWorker?.addEventListener) {
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      }
    };

    const setupForegroundMessaging = async () => {
      if (unsubscribeOnMessage || foregroundSetupStarted) {
        return;
      }
      if (!isPushRuntimeEligible()) {
        return;
      }
      if (Notification.permission !== 'granted') {
        return;
      }

      foregroundSetupStarted = true;

      let supported = false;
      try {
        supported = await isMessagingSupported();
      } catch (error) {
        console.warn('Messaging support check failed:', error);
        supported = false;
      }
      if (!supported || isDisposed) {
        foregroundSetupStarted = false;
        return;
      }

      try {
        const messaging = getMessaging(getFirebaseApp());
        unsubscribeOnMessage = onMessage(messaging, (payload) => {
          console.log('Получено сообщение в переднем плане:', payload);

          const notificationTitle = payload.data?.title || 'Новое уведомление';
          const notificationOptions = {
            body: payload.data?.body || 'Уведомление без текста',
            icon: payload.data?.icon || '/android-icon-96x96.png',
            data: {
              article: payload.data?.article,
              type: payload.data?.type,
              message_id: payload.data?.message_id,
              room_key: payload.data?.room_key,
            },
          };

          const notification = new Notification(notificationTitle, notificationOptions);

          notification.onclick = () => {
            notification.close();
            const data = notification.data || {};
            const article = data.article;
            const type = data.type;
            const messageId = data.message_id;

            let path = '/';
            if (type === 'pcw') {
              path = '/waiting-list';
            } else if (type === 'chat') {
              path = '/chat';
            }

            let url = window.location.origin + path;
            if (article) {
              url = `${window.location.origin}${path}?article=${encodeURIComponent(article)}`;
            } else if (type === 'chat' && messageId) {
              url = `${window.location.origin}${path}?message_id=${encodeURIComponent(messageId)}`;
            }

            window.location.href = url;
          };
        });
      } catch (error) {
        foregroundSetupStarted = false;
        console.error('Ошибка при настройке обработчика уведомлений:', error);
      }
    };

    const handleMaybeSetupForegroundMessaging = () => {
      if (document.visibilityState === 'visible') {
        void setupForegroundMessaging();
      }
    };

    void setupServiceWorker();
    void setupForegroundMessaging();
    void loadLatestChangelog();
    window.addEventListener('focus', handleMaybeSetupForegroundMessaging);
    document.addEventListener('visibilitychange', handleMaybeSetupForegroundMessaging);

    return () => {
      isDisposed = true;
      if (unsubscribeOnMessage) {
        unsubscribeOnMessage();
      }
      if (navigator.serviceWorker?.removeEventListener) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
      window.removeEventListener('focus', handleMaybeSetupForegroundMessaging);
      document.removeEventListener('visibilitychange', handleMaybeSetupForegroundMessaging);
    };
  }, []);

  useEffect(() => {
    const browserData = getBrowserInfo();
    setBrowserInfo(browserData);

    const isInStandaloneMode = isStandaloneMode();
    setIsStandalone(isInStandaloneMode);

    if (!isPwaSupported()) {
      setUnsupportedBrowser(true);
      return;
    }

    if (browserData.isAndroid && browserData.isChrome && !isInStandaloneMode) {
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowInstallPrompt(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }

    if (browserData.isIOS && browserData.isSafari && !isInStandaloneMode) {
      setShowInstallPrompt(true);
    }
  }, []);

  useEffect(() => {
    const browserData = getBrowserInfo();
    const isIosStandalone = Boolean(browserData.isIOS && isStandaloneMode());
    const isAndroidDevice = Boolean(browserData.isAndroid);
    const rootEl = document.documentElement;
    rootEl.classList.toggle('ios-standalone', isIosStandalone);
    rootEl.classList.toggle('android-device', isAndroidDevice);
    rootEl.classList.toggle('glass-disabled', isGlassDisabled);
    return () => {
      rootEl.classList.remove('ios-standalone');
      rootEl.classList.remove('android-device');
      rootEl.classList.remove('glass-disabled');
    };
  }, [isGlassDisabled]);

  useEffect(() => {
    const syncGlassMode = () => {
      setIsGlassDisabled(window.localStorage.getItem(GLASS_MODE_STORAGE_KEY) === 'true');
    };

    window.addEventListener('storage', syncGlassMode);
    window.addEventListener('salew:glass-mode-changed', syncGlassMode);
    return () => {
      window.removeEventListener('storage', syncGlassMode);
      window.removeEventListener('salew:glass-mode-changed', syncGlassMode);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      const result = await deferredPrompt.prompt();
      if (result.outcome === 'accepted') {
        showGlassToast('Установка началась! Проверьте домашний экран');
      }
    } catch (error) {
      showGlassToast('Ошибка установки: ' + error.message, { variant: 'error', duration: 2800 });
    } finally {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const handleCloseModal = () => {
    setShowInstallPrompt(false);
  };

  const handleUpdateApp = async () => {
    if (!navigator.serviceWorker?.getRegistration) {
      window.location.reload();
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(() => window.location.reload(), 1200);
        return;
      }

      await registration?.update?.();
      const nextRegistration = await navigator.serviceWorker.getRegistration();
      if (nextRegistration?.waiting) {
        nextRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(() => window.location.reload(), 1200);
        return;
      }
    } catch (error) {
      console.warn('Не удалось применить обновление Service Worker:', error);
    }

    window.location.reload();
  };

  const BrowserRecommendation = () => {
    if (browserInfo.isInAppBrowser) {
      return (
        <div className="root-glass-alert root-glass-alert--warning">
          <h5>Откройте во внешнем браузере</h5>
          <p>Во встроенном браузере Telegram/VK/Instagram установка PWA и push-уведомления работают нестабильно.</p>
          <p>На iPhone откройте ссылку в Safari, на Android — в Chrome, затем установите приложение на главный экран.</p>
        </div>
      );
    }

    if (browserInfo.isYandex) {
      return (
        <div className="root-glass-alert root-glass-alert--danger">
          <h5>Используйте другой браузер</h5>
          <p>Яндекс.Браузер не поддерживает установку PWA</p>
        </div>
      );
    }

    if (browserInfo.isIOS) {
      return (
        <div className="root-glass-alert root-glass-alert--warning">
          <h5>Для iOS:</h5>
          <ol>
            <li>Откройте сайт в <strong>Safari</strong></li>
            <li>Нажмите <i className="fas fa-share"></i> → "На экран Домой"</li>
            <li>И откройте приложение через иконку на рабочем столе</li>
          </ol>
        </div>
      );
    }

    if (browserInfo.isAndroid) {
      return (
        <div className="root-glass-alert root-glass-alert--warning">
          <h5>Для Android:</h5>
          <p>Требуется Google Chrome</p>
          <a
            href="https://play.google.com/store/apps/details?id=com.android.chrome"
            className="btn btn-success"
            target="_blank"
            rel="noreferrer"
          >
            Установить Chrome
          </a>
          <p>После установки откройте приложение через Chrome</p>
        </div>
      );
    }

    return null;
  };

  return (
    <AuthProvider>
      <GlassToastViewport />
      <FilterProvider>
        <WebSocketProvider>
          <Router>
            <Routes>
              <Route path="/" element={<ProtectedRoute><App /></ProtectedRoute>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/r/:refCode" element={<ReferralLinkPage />} />
              <Route path="/subscribe" element={<SubscriptionForm />} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/market" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
              <Route path="/my-market-items" element={<ProtectedRoute><MyMarketItemsPage /></ProtectedRoute>} />
              <Route path="/waiting-list" element={<ProtectedRoute><WaitingListPage /></ProtectedRoute>} />
              <Route path="/yesterday-sales" element={<YesterdaySales
                closeModals={() => {
                  setShowUpdateModal(false);
                  setShowInstallPrompt(false);
                  setUnsupportedBrowser(false);
                }}
              />} />
              <Route path="/videos" element={<VideosPage
                closeModals={() => {
                  setShowUpdateModal(false);
                  setShowInstallPrompt(false);
                  setUnsupportedBrowser(false);
                }}
              />} />
            </Routes>
          </Router>
        </WebSocketProvider>
      </FilterProvider>
      <Modal
        show={showUpdateModal}
        onHide={() => {}}
        centered
        backdrop="static"
        keyboard={false}
        className={`root-update-modal ${theme === 'dark' ? 'root-update-modal--dark modal-dark' : 'root-update-modal--light modal-light'}`}
        contentClassName="root-update-modal__content"
        backdropClassName="root-update-modal__backdrop"
      >
        <Modal.Header className="root-update-modal__header">
          <Modal.Title className="root-update-modal__title">Доступно обновление</Modal.Title>
        </Modal.Header>
        <Modal.Body className="root-update-modal__body">
          {latestChangelog && (
            <div className="root-update-card">
              <div className="root-update-card__meta">
                {latestChangelog.version && <span>v{latestChangelog.version}</span>}
                {latestChangelog.date && <span>{latestChangelog.date}</span>}
              </div>
              <h3>{latestChangelog.title || 'Что нового'}</h3>
              <ul>
                {(latestChangelog.items || []).map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="root-update-note">
            Обновите приложение, чтобы изменения применились без ошибок кэша. Старую версию лучше не оставлять открытой.
          </p>
          <div className="root-update-actions">
            <Button
              variant="primary"
              className="root-update-btn root-update-btn--primary"
              onClick={() => { void handleUpdateApp(); }}
            >
              Обновить сейчас
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showInstallPrompt} onHide={() => setShowInstallPrompt(false)} centered>
        <Modal.Header closeButton className={themeClass}>
          <Modal.Title>Установка приложения</Modal.Title>
        </Modal.Header>
        <Modal.Body className={themeClass}>
          {browserInfo.isIOS ? (
            <div>
              <div className="root-glass-alert root-glass-alert--info d-flex align-items-center">
                <i className="fab fa-apple me-2 fs-4"></i>
                <span>Инструкция для iOS</span>
              </div>
              <div className="d-grid gap-2">
              </div>
              <div className="mt-3">
                {[1, 2, 3].map(step => (
                  <div key={step} className="d-flex align-items-center mb-3">
                    <div className="me-2 badge bg-primary">{step}</div>
                    <span>
                      {step === 1 && 'Откройте сайт в Safari'}
                      {step === 2 && 'Нажмите "Поделиться" → "На экран Домой"'}
                      {step === 3 && 'Появится иконка на рабочем столе.'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="mb-4">Установите приложение для лучшего опыта!</p>
              <Button variant="primary" size="lg" onClick={handleInstallClick}>
                <i className="fas fa-download me-2"></i>
                Установить сейчас
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>


      <Modal show={unsupportedBrowser} onHide={() => setUnsupportedBrowser(false)} centered>
        <Modal.Header closeButton className={themeClass}>
          <Modal.Title>⚠️ Требуется действие</Modal.Title>
        </Modal.Header>
        <Modal.Body className={themeClass}>
          <div className="text-center">
            <BrowserRecommendation />
          </div>
        </Modal.Body>
      </Modal>
    </AuthProvider>
  );
};

export default Root;
